import type {
  ConditionEvaluationResult,
  ValuesAtExecution,
} from "@redotech/effect-api-shared/models/shopper-ai/rule-engine-results";
import type {
  ConditionGroupV3,
  ConditionGroupV3Operator,
  RuleV3Condition,
} from "@redotech/redo-model/advanced-flow/rules-v3/schemas";
import type { RuleTriggerType } from "@redotech/redo-model/advanced-flow/rules/types";
import {
  QueryOperator,
  RuleConditionType,
} from "@redotech/redo-model/advanced-flow/rules/types";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback } from "react";
import { useDefaultRuleEditContext } from "../default-rule-edit-context";
import { ConditionListV3 } from "./condition-list-v3";

export interface ExecutionData {
  passed: boolean;
  valuesAtExecution: ValuesAtExecution;
  results: ConditionEvaluationResult[];
}

export interface ConditionGroupEditorV3Props {
  group: ConditionGroupV3;
  onChange: (group: ConditionGroupV3) => void;
  triggerType?: RuleTriggerType;
  executionData?: ExecutionData;
}

function isLeafCondition(
  item: RuleV3Condition | ConditionGroupV3,
): item is RuleV3Condition {
  return !("operator" in item);
}

export const ConditionGroupEditorV3 = memo(function ConditionGroupEditorV3({
  group,
  onChange,
  triggerType,
  executionData,
}: ConditionGroupEditorV3Props) {
  const operatorOptions: {
    id: string;
    type: "text";
    text: string;
    value: ConditionGroupV3Operator;
  }[] = [
    { id: "AND", type: "text", text: "All", value: "AND" },
    { id: "OR", type: "text", text: "Any", value: "OR" },
  ];

  const handleOperatorChange = useCallback(
    (operator: ConditionGroupV3Operator) => {
      onChange({ ...group, operator });
    },
    [group, onChange],
  );

  const handleConditionsChange = useCallback(
    (conditions: RuleV3Condition[]) => {
      onChange({ ...group, conditions });
    },
    [group, onChange],
  );

  const handleAddCondition = useCallback(() => {
    const newCondition: RuleV3Condition = {
      conditionType: RuleConditionType.TICKET_CHANNEL,
      channels: [],
      queryOperator: QueryOperator.IS,
    };
    onChange({ ...group, conditions: [...group.conditions, newCondition] });
  }, [group, onChange]);

  // Default-rule edit mode locks the structural controls (operator,
  // add-condition, nested groups). Leaf editability is handled by
  // `ConditionItemV3` via its own context consumption.
  const isDefaultRuleEdit = useDefaultRuleEditContext() !== null;
  const readonly = !!executionData || isDefaultRuleEdit;

  return (
    <Flex bgColor="primary" dir="column" gap="md" p="md" radius="sm">
      <Flex align="center" gap="sm">
        <RedoSingleSelectDropdownInput
          disabled={readonly}
          options={operatorOptions}
          optionSelected={({ item }) => handleOperatorChange(item.value)}
          selectedItem={operatorOptions.find(
            (opt) => opt.value === group.operator,
          )}
        />
        <Text color="secondary" fontSize="sm">
          of these conditions must match
        </Text>
      </Flex>

      <Text fontSize="sm" fontWeight="medium">
        Conditions
      </Text>

      <ConditionListV3
        conditions={group.conditions.filter(isLeafCondition)}
        executionData={executionData}
        onChange={handleConditionsChange}
        operator={group.operator}
        readonly={readonly}
        triggerType={triggerType}
      />

      {!readonly && (
        <Flex gap="sm">
          <RedoButton
            hierarchy="tertiary"
            onClick={handleAddCondition}
            size="sm"
            text="+ Condition"
          />
        </Flex>
      )}
    </Flex>
  );
});
